/**
 * Shared helpers for placing context banners ("Reading documents…" / case attached)
 * inline with chat messages.
 *
 * The chat thread shows a banner each time the user attaches new context (a case
 * or one or more documents) right above the message that was sent with it. With
 * multiple attach turns this means **multiple** banners — one per turn — each
 * containing only the deltas for that turn. The earlier design kept a single
 * coalesced banner pinned to the earliest attach, which made docs added in
 * later turns appear above the wrong (older) message.
 *
 * Grouping rule
 * -------------
 *
 * For each attach event (per-document upload, or the case link) we know the
 * timestamp at which it happened (``documents[].created_at`` /
 * ``case_linked_at`` for persisted state, or a frontend ``Date.now()`` recorded
 * when the user pushed an annotation). The target message for that attach is
 * the **first** message whose ``created_at`` is at or after the attach time —
 * i.e. the message the user sent with the attached context. Events whose
 * timestamp is later than every existing message land in a synthetic
 * "after-end" group that renders below the last message (above the composer's
 * next send).
 *
 * Both surfaces (side-panel ``useChatStream``, fullscreen ``AiChatMain``) feed
 * their own message list through this helper so they stay aligned.
 */

export interface ContextAttachDoc {
  uuid: string
  filename: string
}

/**
 * One attach event in the session's timeline.
 *
 * - ``kind: 'doc'`` represents a single document upload.
 * - ``kind: 'case'`` represents the (single) case-link event.
 */
export type ContextAttachEvent =
  | { kind: 'doc'; epoch: number; doc: ContextAttachDoc }
  | { kind: 'case'; epoch: number; caseLabel: string; caseUuid?: string }

/**
 * A grouped banner ready for rendering.
 *
 * :ivar insertIdx: Index into the message list where this banner should appear.
 *     ``msgs.length`` means "render after the last message" (i.e. the user has
 *     attached context but not yet sent the next message).
 */
export interface ContextGroup {
  insertIdx: number
  caseLabel?: string
  caseUuid?: string
  documents: ContextAttachDoc[]
}

/**
 * Parse an ISO/UTC timestamp into epoch ms.
 *
 * Backend timestamps may use mixed timezone offsets (messages in UTC,
 * documents in local time), so always go through ``Date.parse`` rather than
 * lexicographic ISO ordering.
 *
 * :param t: Backend timestamp string, or ``null``/``undefined`` for unknown.
 * :return: Epoch milliseconds, or ``null`` when the value is missing or
 *     unparseable.
 */
export function parseTimestamp(t: string | null | undefined): number | null {
  if (!t) return null
  const ms = Date.parse(t)
  return Number.isNaN(ms) ? null : ms
}

/**
 * Per-message slot used by :func:`groupAttachesByTargetMessage` to locate the
 * "owning" message for an attach event.
 *
 * :ivar epoch: ``created_at`` parsed to epoch ms; ``null`` when missing.
 * :ivar targetable: Whether this message can be the *target* of a banner.
 *     Only user messages are targetable — banners always sit above the user
 *     message that was sent with the attached context, never above an
 *     assistant reply that happened to land between the upload and the
 *     follow-up send.
 */
export interface MessageSlot {
  epoch: number | null
  targetable: boolean
}

/**
 * Group attach events into one banner per target message.
 *
 * :param msgs: Per-message slots in thread order. The slot ``epoch`` is
 *     compared to the event's epoch to pick the target; the array index of
 *     the first ``targetable`` slot whose epoch is at or after the event time
 *     becomes the banner's ``insertIdx``. Slots with ``targetable: false``
 *     (typically assistant messages) are skipped during the lookup so they
 *     never receive the banner, but are still counted toward the array index
 *     so the result lines up with the rendered thread.
 * :param events: All attach events in the session, in any order. Duplicate
 *     document uuids are folded into the first matching group; only the first
 *     ``case`` event is retained (the backend invariant guarantees at most
 *     one case per session, but transient + persisted streams may both emit
 *     one).
 * :return: Groups sorted by ``insertIdx``, ready to splice into the rendered
 *     thread.
 */
export function groupAttachesByTargetMessage(
  msgs: MessageSlot[],
  events: ContextAttachEvent[]
): ContextGroup[] {
  /**
   * Resolve the insert index for a single attach event.
   *
   * The primary pass picks the first targetable (user) message whose
   * ``created_at`` is at or after the event time — i.e. the message the user
   * sent with that context attached.
   *
   * Case-event fallback (race tolerance):
   * The side-panel attach flow PATCHes ``case_uuid`` (REST) and sends the
   * user message (WebSocket) concurrently. The WS hop is ~1ms while a fresh
   * REST PATCH is 50–200ms, so the backend regularly records the new user
   * message **before** ``case_linked_at`` even though the user logically
   * attached the case alongside that send. The standard pass would then
   * dump the case banner below the assistant reply (no targetable msg has
   * ``epoch >= case_linked_at`` — only the assistant's later
   * ``created_at`` does). When we detect that exact bracket — at least one
   * non-targetable message later than the event AND a prior targetable
   * message — we snap the case banner to the most recent prior user
   * message so it lands above the turn that introduced the case.
   *
   * Doc events deliberately keep the strict pass: a document uploaded
   * mid-stream while the assistant is still replying must wait for the
   * next user send (the user has not asked the assistant to consider it
   * yet). Applying the same fallback to docs would yank that banner up
   * above the wrong (earlier) user message.
   *
   * Pure "case attached, no send yet" attaches still return ``msgs.length``
   * — every message epoch is before the event, so neither the standard
   * pass nor the bracket fallback matches.
   */
  const targetIdxFor = (event: ContextAttachEvent): number => {
    const eventEpoch = event.epoch
    for (let i = 0; i < msgs.length; i++) {
      const m = msgs[i]
      if (!m.targetable || m.epoch === null) continue
      if (m.epoch >= eventEpoch) return i
    }

    if (event.kind === 'case') {
      let lastTargetableIdx = -1
      let bracketed = false
      for (let i = 0; i < msgs.length; i++) {
        const m = msgs[i]
        if (m.epoch === null) continue
        if (m.targetable) {
          lastTargetableIdx = i
          continue
        }
        if (m.epoch >= eventEpoch && lastTargetableIdx !== -1) {
          bracketed = true
          break
        }
      }
      if (bracketed) return lastTargetableIdx
    }

    return msgs.length
  }

  const groups = new Map<number, ContextGroup>()
  const seenDocUuids = new Set<string>()
  let caseAssigned = false

  // Stable order (oldest event first) so when two events target the same
  // message the case row appears above the doc row in the banner.
  const ordered = [...events].sort((a, b) => a.epoch - b.epoch)

  for (const e of ordered) {
    const idx = targetIdxFor(e)
    let g = groups.get(idx)
    if (!g) {
      g = { insertIdx: idx, documents: [] }
      groups.set(idx, g)
    }
    if (e.kind === 'doc') {
      if (seenDocUuids.has(e.doc.uuid)) continue
      seenDocUuids.add(e.doc.uuid)
      g.documents.push(e.doc)
    } else {
      if (caseAssigned) continue
      caseAssigned = true
      g.caseLabel = e.caseLabel
      g.caseUuid = e.caseUuid
    }
  }

  return Array.from(groups.values()).sort((a, b) => a.insertIdx - b.insertIdx)
}
